import React, { useEffect, useRef, useState } from 'react';
import type { Question } from '@examflow/core';
import type { WritingExpressionCategoryId } from '@examflow/exams';
import { Button, useToast } from '@examflow/ui';
import {
  writingDraftStore,
  type WritingDraftGradeSession,
  type WritingDraftGradeSnapshot,
  type WritingDraftScope,
} from '../../data/local';
import { api, ApiError } from '../../lib/api';
import { UpgradePrompt } from '../../components/UpgradePrompt';
import { insertAtCaret } from './insertAtCaret';
import {
  countEssayWords,
  FRENCH_SPECIAL_CHARS_LOWER,
  FRENCH_SPECIAL_CHARS_UPPER,
  pickWeakestWritingDimension,
  resolveOpenEndedMeta,
  resolveWritingDisplayLabels,
} from './openEndedWriting';
import { SessionClock } from './SessionClock';
import { WritingExpressionAid } from './WritingExpressionAid';
import { WritingGradePanel, type WritingGradeView } from './WritingGradePanel';
import {
  expressionCategoriesForWritingDimension,
  pickInlineWritingExpressions,
} from './writingExpressionAidModel';

export type { WritingGradeView };

export type WritingSessionCompletePayload = {
  essay: string;
  wordCount: number;
  grade: WritingGradeView;
  timeSpentMs: number;
  revisionCount: number;
  previousEssay?: string;
  previousScore?: number;
};

type QuotaPromptState = {
  reason: 'no_entitlement' | 'quota_exhausted';
  resetAt?: string;
};

type WritingSessionPanelProps = {
  question: Question;
  questionNumber: number;
  languageId: string | null;
  examId: string;
  skillId: string;
  /** When set, AI grade session is restored/persisted with the local essay draft. */
  draftScope?: WritingDraftScope | null;
  draftText: string;
  /** Epoch ms after a successful local draft persist; null = not saved yet. */
  draftSavedAt: number | null;
  /** True while a debounced Dexie write is in flight. */
  draftSavePending?: boolean;
  elapsedSeconds: number;
  /** Writing soft clock — pause/reset only (never a hard gate). */
  clockPaused?: boolean;
  onToggleClockPause?: () => void;
  onResetClock?: () => void;
  onDraftChange: (text: string) => void;
  /** Persist evidence + leave session — only when learner ends the revision loop. */
  onComplete: (payload: WritingSessionCompletePayload) => void;
  onViewHistory?: () => void;
};

function parseQuotaPrompt(error: unknown): QuotaPromptState | null {
  if (!(error instanceof ApiError) || error.statusCode !== 402) return null;
  const body = (error.body ?? {}) as { error?: string; resetAt?: string };
  const reason =
    body.error === 'no_entitlement' || body.error === 'quota_exhausted'
      ? body.error
      : 'quota_exhausted';
  return { reason, resetAt: body.resetAt };
}

function toGradeView(snapshot: WritingDraftGradeSnapshot): WritingGradeView {
  return { ...snapshot };
}

function toGradeSnapshot(grade: WritingGradeView): WritingDraftGradeSnapshot {
  return {
    score: grade.score,
    scoreMax: grade.scoreMax,
    dimensionMax: grade.dimensionMax,
    scaleLabel: grade.scaleLabel,
    levelHint: grade.levelHint,
    feedback: grade.feedback,
    strengths: [...grade.strengths],
    improvements: [...grade.improvements],
    dimensions: grade.dimensions.map((dim) => ({ ...dim })),
    isHeuristic: grade.isHeuristic,
  };
}

function buildGradeSession(input: {
  gradeResult: WritingGradeView;
  firstDraft: string | null;
  firstGrade: WritingGradeView | null;
  isRevising: boolean;
  hasCompletedRevision: boolean;
  revisionCredit: string | null;
}): WritingDraftGradeSession {
  return {
    gradeResult: toGradeSnapshot(input.gradeResult),
    firstDraft: input.firstDraft,
    firstGrade: input.firstGrade ? toGradeSnapshot(input.firstGrade) : null,
    isRevising: input.isRevising,
    hasCompletedRevision: input.hasCompletedRevision,
    revisionCredit: input.revisionCredit,
  };
}

export function WritingSessionPanel({
  question,
  languageId,
  examId,
  skillId,
  draftScope = null,
  draftText,
  draftSavedAt,
  draftSavePending = false,
  elapsedSeconds,
  clockPaused = false,
  onToggleClockPause,
  onResetClock,
  onDraftChange,
  onComplete,
  onViewHistory,
}: WritingSessionPanelProps) {
  const toast = useToast();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [uppercase, setUppercase] = useState(false);
  const [isGrading, setIsGrading] = useState(false);
  const [gradeResult, setGradeResult] = useState<WritingGradeView | null>(null);
  const [firstDraft, setFirstDraft] = useState<string | null>(null);
  const [firstGrade, setFirstGrade] = useState<WritingGradeView | null>(null);
  const [isRevising, setIsRevising] = useState(false);
  const [hasCompletedRevision, setHasCompletedRevision] = useState(false);
  const [quotaPrompt, setQuotaPrompt] = useState<QuotaPromptState | null>(null);
  /** One free revision re-grade after a paid AI score. */
  const [revisionCredit, setRevisionCredit] = useState<string | null>(null);
  const [expressionAidOpen, setExpressionAidOpen] = useState(false);
  const [expressionFocusCategory, setExpressionFocusCategory] =
    useState<WritingExpressionCategoryId | null>(null);
  const startedAtRef = useRef(Date.now());

  const meta = resolveOpenEndedMeta(question, examId);
  const display = resolveWritingDisplayLabels(question, examId);
  const wordCount = countEssayWords(draftText);
  const belowMin = wordCount < meta.minWordCount;
  const aboveMax = meta.maxWordCount != null && wordCount > meta.maxWordCount;
  const specialChars = uppercase ? FRENCH_SPECIAL_CHARS_UPPER : FRENCH_SPECIAL_CHARS_LOWER;
  const showFrenchKeyboard = languageId === 'french';
  const editorLocked = (!!gradeResult && !isRevising) || isGrading;
  const weakestDimension = gradeResult
    ? pickWeakestWritingDimension(gradeResult.dimensions)
    : null;

  const persistGradeSession = (
    essay: string,
    session: WritingDraftGradeSession | null
  ) => {
    if (!draftScope || !essay.trim()) return;
    void writingDraftStore.upsert(draftScope, question.id, essay, { gradeSession: session });
  };

  useEffect(() => {
    startedAtRef.current = Date.now();
    setGradeResult(null);
    setFirstDraft(null);
    setFirstGrade(null);
    setIsRevising(false);
    setHasCompletedRevision(false);
    setQuotaPrompt(null);
    setRevisionCredit(null);
    setExpressionAidOpen(false);
    setExpressionFocusCategory(null);

    if (!draftScope) return;
    let cancelled = false;
    void writingDraftStore.get(draftScope, question.id).then((record) => {
      if (cancelled) return;
      const session = record?.gradeSession;
      if (!session?.gradeResult) return;
      setGradeResult(toGradeView(session.gradeResult));
      setFirstDraft(session.firstDraft);
      setFirstGrade(session.firstGrade ? toGradeView(session.firstGrade) : null);
      setIsRevising(session.isRevising);
      setHasCompletedRevision(session.hasCompletedRevision);
      setRevisionCredit(session.revisionCredit);
    });
    return () => {
      cancelled = true;
    };
  }, [question.id, draftScope]);

  const draftStatusLabel = (() => {
    if (!draftText.trim()) return '尚未开始写作';
    if (draftSavePending) return '正在保存草稿…';
    if (draftSavedAt) {
      const time = new Date(draftSavedAt).toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit',
      });
      return `已保存草稿 · ${time}`;
    }
    return '草稿未保存';
  })();

  const insertText = (chunk: string) => {
    const el = textareaRef.current;
    if (!el) {
      const { next } = insertAtCaret(draftText, chunk, draftText.length, draftText.length);
      onDraftChange(next);
      return;
    }
    const start = el.selectionStart ?? draftText.length;
    const end = el.selectionEnd ?? draftText.length;
    const { next, caret } = insertAtCaret(draftText, chunk, start, end);
    onDraftChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  const handleBrowseExpressions = (category: WritingExpressionCategoryId) => {
    setExpressionFocusCategory(category);
    setExpressionAidOpen(true);
    if (gradeResult && !isRevising) {
      setIsRevising(true);
      persistGradeSession(
        draftText,
        buildGradeSession({
          gradeResult,
          firstDraft,
          firstGrade,
          isRevising: true,
          hasCompletedRevision,
          revisionCredit,
        })
      );
      toast.info(
        revisionCredit
          ? '已打开常用表达；修订后再评一次看进步（本会话修订再评免费一次）'
          : '已打开常用表达；修订后再评一次看进步'
      );
    }
  };

  const handleInsertExpression = (text: string) => {
    const cats = expressionCategoriesForWritingDimension(weakestDimension?.id);
    if (gradeResult && !isRevising) {
      setIsRevising(true);
      setExpressionFocusCategory(cats[0] ?? null);
      persistGradeSession(
        draftText,
        buildGradeSession({
          gradeResult,
          firstDraft,
          firstGrade,
          isRevising: true,
          hasCompletedRevision,
          revisionCredit,
        })
      );
      toast.info(
        revisionCredit
          ? '已插入表达；修订后再评一次看进步（本会话修订再评免费一次）'
          : '已插入表达；修订后再评一次看进步'
      );
    }
    insertText(text);
  };

  const handleGrade = async () => {
    if (belowMin || isGrading || (gradeResult && !isRevising)) return;
    setIsGrading(true);
    setQuotaPrompt(null);
    try {
      const response = await api.ai.gradeWriting({
        examId,
        skillId,
        prompt: question.content,
        essay: draftText.trim(),
        wordCount,
        taskNumber: meta.taskNumber,
        minWordCount: meta.minWordCount,
        maxWordCount: meta.maxWordCount,
        languageId,
        previousEssay: isRevising ? firstDraft : null,
        previousScore: isRevising ? firstGrade?.score : null,
        revisionCredit: isRevising ? revisionCredit : null,
      });
      const grade: WritingGradeView = {
        score: response.grade.score,
        scoreMax: response.grade.scoreMax ?? 20,
        dimensionMax: response.grade.dimensionMax ?? 5,
        scaleLabel: response.grade.scaleLabel ?? '0–20',
        levelHint: response.grade.levelHint,
        feedback: response.grade.feedback,
        strengths: response.grade.strengths ?? [],
        dimensions: response.grade.dimensions ?? [],
        improvements: (response.grade.improvements ?? []).slice(0, 3),
        isHeuristic: response.grade.isHeuristic === true,
      };
      let nextRevisionCredit = revisionCredit;
      if (response.usedRevisionCredit) {
        nextRevisionCredit = null;
        setRevisionCredit(null);
      } else if (typeof response.revisionCredit === 'string' && response.revisionCredit.trim()) {
        nextRevisionCredit = response.revisionCredit.trim();
        setRevisionCredit(nextRevisionCredit);
      } else if (grade.isHeuristic) {
        nextRevisionCredit = null;
        setRevisionCredit(null);
      }
      const nextFirstDraft = firstGrade ? firstDraft : draftText.trim();
      const nextFirstGrade = firstGrade ?? grade;
      const nextHasCompletedRevision = firstGrade && isRevising ? true : hasCompletedRevision;
      if (!firstGrade) {
        setFirstDraft(nextFirstDraft);
        setFirstGrade(nextFirstGrade);
      } else if (isRevising) {
        setHasCompletedRevision(true);
      }
      setGradeResult(grade);
      setIsRevising(false);
      persistGradeSession(
        draftText,
        buildGradeSession({
          gradeResult: grade,
          firstDraft: nextFirstDraft,
          firstGrade: nextFirstGrade,
          isRevising: false,
          hasCompletedRevision: nextHasCompletedRevision,
          revisionCredit: nextRevisionCredit,
        })
      );
      if (grade.isHeuristic) {
        toast.info(`参考分 ${grade.score}/${grade.scoreMax}（AI 评语暂不可用）`);
      } else if (response.usedRevisionCredit) {
        toast.success(`修订评分完成：${grade.score}/${grade.scoreMax}（本次未扣配额）`);
      } else {
        toast.success(`评分完成：${grade.score}/${grade.scoreMax}`);
      }
    } catch (error) {
      const quota = parseQuotaPrompt(error);
      if (quota) {
        setQuotaPrompt(quota);
        return;
      }
      toast.error(error instanceof Error ? error.message : '写作评分失败，请稍后重试');
    } finally {
      setIsGrading(false);
    }
  };

  const handleStartRevision = () => {
    if (!gradeResult) return;
    const cats = expressionCategoriesForWritingDimension(weakestDimension?.id);
    setIsRevising(true);
    setExpressionFocusCategory(cats[0] ?? null);
    setExpressionAidOpen(true);
    persistGradeSession(
      draftText,
      buildGradeSession({
        gradeResult,
        firstDraft,
        firstGrade,
        isRevising: true,
        hasCompletedRevision,
        revisionCredit,
      })
    );
    toast.info(
      revisionCredit
        ? '对照改法修订后，再评一次看进步（本会话修订再评免费一次）'
        : '对照改法修订后，再评一次看进步'
    );
  };

  const handleComplete = () => {
    if (!gradeResult) return;
    onComplete({
      essay: draftText.trim(),
      wordCount: countEssayWords(draftText),
      grade: gradeResult,
      timeSpentMs: Date.now() - startedAtRef.current,
      revisionCount: hasCompletedRevision ? 1 : 0,
      previousEssay: firstDraft ?? undefined,
      previousScore: firstGrade?.score,
    });
  };

  const scoreDelta =
    firstGrade && gradeResult && firstDraft && draftText.trim() !== firstDraft
      ? Math.round((gradeResult.score - firstGrade.score) * 10) / 10
      : null;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span
            className={`rounded-md px-2 py-0.5 text-xs font-medium ${
              display.taskUnresolved
                ? 'bg-amber-50 text-amber-800 ring-1 ring-amber-200/80'
                : 'bg-slate-100 text-slate-700'
            }`}
          >
            {display.taskLabel}
          </span>
          {display.suggestedTimeLabel && (
            <span className="rounded-md bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-slate-200/80">
              {display.suggestedTimeLabel}
            </span>
          )}
          {display.topicLabels.map((label) => (
            <span
              key={label}
              className="rounded-md bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-slate-200/80"
            >
              {label}
            </span>
          ))}
          {display.levelLabel && (
            <span className="rounded-md bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-slate-200/80">
              {display.levelLabel}
            </span>
          )}
          {isRevising && (
            <span className="rounded-md bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700">
              修订中
            </span>
          )}
          {onViewHistory && (
            <button
              type="button"
              onClick={onViewHistory}
              className="ml-auto text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              查看历史评分
            </button>
          )}
        </div>
        <div className="whitespace-pre-wrap text-[15px] leading-7 text-slate-800">{question.content}</div>
        {(display.taskHint || display.wordWindowLabel) && (
          <p className="mt-3 text-sm text-slate-500">
            {[display.taskHint, display.wordWindowLabel]
              .filter(Boolean)
              .filter((part, index, arr) => arr.indexOf(part) === index)
              .join(' · ')}
          </p>
        )}
      </div>

      {showFrenchKeyboard && !editorLocked && (
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">法语特殊字符</span>
            <button
              type="button"
              onClick={() => setUppercase((value) => !value)}
              className={`rounded-md px-2.5 py-1 text-xs font-semibold ${
                uppercase ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-700'
              }`}
            >
              大写
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {specialChars.map((char) => (
              <button
                key={char}
                type="button"
                onClick={() => insertText(char)}
                className="min-w-[2rem] rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-sm font-medium text-slate-800 hover:border-primary-300 hover:bg-primary-50"
              >
                {char}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-4 py-2.5 text-sm text-slate-600">
          <SessionClock
            variant="softTarget"
            ms={elapsedSeconds * 1000}
            targetMs={
              display.suggestedMinutes != null ? display.suggestedMinutes * 60_000 : null
            }
            paused={clockPaused}
            onTogglePause={onToggleClockPause}
            onReset={onResetClock}
          />
          <span>
            当前: {wordCount} 词
            {belowMin && (
              <span className="ml-2 text-warning-600">还差 {meta.minWordCount - wordCount} 词</span>
            )}
            {aboveMax && <span className="ml-2 text-warning-600">已超过建议上限</span>}
            <span className="ml-2 text-slate-400">（{display.wordWindowLabel}）</span>
          </span>
          {!editorLocked ? (
            <div className="ml-auto">
              <WritingExpressionAid
                languageId={languageId}
                open={expressionAidOpen}
                onOpenChange={setExpressionAidOpen}
                focusCategory={expressionFocusCategory}
                onInsert={insertText}
              />
            </div>
          ) : null}
        </div>
        <textarea
          ref={textareaRef}
          value={draftText}
          onChange={(event) => onDraftChange(event.target.value)}
          disabled={editorLocked}
          rows={14}
          placeholder="在此写作…"
          className="w-full resize-y border-0 bg-transparent px-4 py-3 text-[15px] leading-7 text-slate-900 outline-none placeholder:text-slate-400 disabled:bg-slate-50"
        />
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/60 px-4 py-3">
          <span className="text-xs text-slate-500">{draftStatusLabel}</span>
          {(!gradeResult || isRevising) && (
            <Button
              type="button"
              size="md"
              onClick={() => void handleGrade()}
              disabled={belowMin}
              isLoading={isGrading}
              loadingLabel="评分中"
              className="min-w-[9.5rem] shadow-sm"
            >
              {isRevising
                ? revisionCredit
                  ? '再评修订稿 · 免费'
                  : '再评修订稿'
                : '改卷评分'}
            </Button>
          )}
        </div>
      </div>

      {gradeResult ? (
        <WritingGradePanel
          grade={gradeResult}
          weakestDimension={weakestDimension}
          scoreDelta={scoreDelta}
          firstDraft={firstDraft}
          firstGrade={firstGrade}
          currentDraft={draftText}
          hasCompletedRevision={hasCompletedRevision}
          revisionCredit={revisionCredit}
          revisionMode={isRevising}
          inlineExpressions={pickInlineWritingExpressions(
            languageId,
            weakestDimension?.id
          ).map((item) => ({
            id: item.id,
            text: item.text,
            gloss: item.gloss,
          }))}
          onInsertExpression={handleInsertExpression}
          expressionBrowseCategory={
            expressionCategoriesForWritingDimension(weakestDimension?.id)[0] ?? null
          }
          onBrowseExpressions={handleBrowseExpressions}
          onStartRevision={handleStartRevision}
          onComplete={handleComplete}
        />
      ) : null}

      {quotaPrompt && (
        <UpgradePrompt
          metric="ai_writing"
          reason={quotaPrompt.reason}
          resetAt={quotaPrompt.resetAt}
        />
      )}
    </div>
  );
}
